import googleSheetsService from '../services/googleSheets';
import { PricingSlab } from '../types';

// Re-export for backward compatibility
export type { PricingSlab } from '../types';

export interface PricingResult {
  totalEmails: number;
  emailsInLakhs: number;
  monthlyPrice: number;
  discountPercentage: number;
  discountAmount: number;
  discountedPrice: number;
  currency: 'INR' | 'USD';
  slab: PricingSlab;
}

export interface QuoteData {
  quoteNumber: string;
  quoteDate: string;
  customerName: string;
  companyName: string;
  emailVolume: number;
  pricing: PricingResult;
  discountReason?: string;
  generatedBy?: {
    name: string;
    email: string;
  };
}

// Cache for pricing slabs to avoid repeated API calls
let cachedPricingSlabs: {
  INR: PricingSlab[] | null;
  USD: PricingSlab[] | null;
  lastFetch: number;
} = {
  INR: null,
  USD: null,
  lastFetch: 0
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Fetch pricing slabs from Google Sheets
export const getPricingSlabs = async (currency: 'INR' | 'USD'): Promise<PricingSlab[]> => {
  const now = Date.now();
  
  // Check cache
  if (cachedPricingSlabs[currency] && (now - cachedPricingSlabs.lastFetch) < CACHE_DURATION) {
    return cachedPricingSlabs[currency]!;
  }
  
  try {
    const slabs = await googleSheetsService.getPricingSlabsByCurrency(currency);
    cachedPricingSlabs[currency] = slabs;
    cachedPricingSlabs.lastFetch = now;
    return slabs;
  } catch (error) {
    console.error('Failed to fetch pricing slabs:', error);
    throw new Error('Unable to load pricing data. Please check your connection.');
  }
};

// Clear cache when needed
export const clearPricingCache = () => {
  cachedPricingSlabs = {
    INR: null,
    USD: null,
    lastFetch: 0
  };
};

export const calculatePricing = async (totalEmails: number, currency: 'INR' | 'USD' = 'INR', discountPercentage: number = 0): Promise<PricingResult> => {
  const slabs = await getPricingSlabs(currency);

  if (totalEmails <= 0 || slabs.length === 0) {
    return {
      totalEmails: 0,
      emailsInLakhs: 0,
      monthlyPrice: 0,
      discountPercentage: 0,
      discountAmount: 0,
      discountedPrice: 0,
      currency,
      slab: slabs[0] || {
        slabId: '',
        currency: currency,
        minEmails: 0,
        maxEmails: null,
        pricePerLakh: 0,
        label: 'No pricing data',
        isActive: false,
        createdDate: '',
        updatedDate: ''
      }
    };
  }

  // Calculate emails in lakhs (1 lakh = 100,000 emails)
  const emailsInLakhs = totalEmails / 100000;

  // Validate discount percentage (0-50%)
  const validDiscountPercentage = Math.max(0, Math.min(50, discountPercentage));
  
  // Find the matching slab for the total email volume
  for (const slab of slabs) {
    const maxEmails = slab.maxEmails || Infinity;
    
    if (totalEmails >= slab.minEmails && totalEmails <= maxEmails) {
      // Calculate monthly price: emails in lakhs × price per lakh
      const monthlyPrice = emailsInLakhs * slab.pricePerLakh;
      
      // Calculate discount amount and final discounted price
      const discountAmount = (monthlyPrice * validDiscountPercentage) / 100;
      const discountedPrice = monthlyPrice - discountAmount;
      
      return {
        totalEmails,
        emailsInLakhs: Math.round(emailsInLakhs * 100) / 100, // Round to 2 decimals
        monthlyPrice: Math.round(monthlyPrice * 100) / 100, // Round to 2 decimals
        discountPercentage: validDiscountPercentage,
        discountAmount: Math.round(discountAmount * 100) / 100, // Round to 2 decimals
        discountedPrice: Math.round(discountedPrice * 100) / 100, // Round to 2 decimals
        currency,
        slab
      };
    }
  }

  // Fallback to the highest slab if no match found
  const highestSlab = slabs[slabs.length - 1];
  const monthlyPrice = emailsInLakhs * highestSlab.pricePerLakh;
  const discountAmount = (monthlyPrice * validDiscountPercentage) / 100;
  const discountedPrice = monthlyPrice - discountAmount;
  
  return {
    totalEmails,
    emailsInLakhs: Math.round(emailsInLakhs * 100) / 100,
    monthlyPrice: Math.round(monthlyPrice * 100) / 100,
    discountPercentage: validDiscountPercentage,
    discountAmount: Math.round(discountAmount * 100) / 100,
    discountedPrice: Math.round(discountedPrice * 100) / 100,
    currency,
    slab: highestSlab
  };
};

export const formatCurrency = (amount: number, currency: 'INR' | 'USD'): string => {
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return formatter.format(amount);
};

export const formatEmailCount = (count: number): string => {
  if (count >= 10000000) { // 1 crore and above
    const crores = count / 10000000;
    return crores >= 1 ? `${crores.toFixed(crores >= 10 ? 0 : 1)} cr` : `${(count / 100000).toFixed(0)} lakh`;
  } else if (count >= 100000) { // 1 lakh and above
    const lakhs = count / 100000;
    return `${lakhs.toFixed(lakhs >= 10 ? 0 : 1)} lakh`;
  } else if (count >= 1000) { // 1K and above
    return `${(count / 1000).toFixed(0)}K`;
  }
  return count.toString();
};

export const formatEmailCountShort = (count: number): string => {
  if (count >= 10000000) { // 1 crore
    return `${(count / 10000000).toFixed(1)}cr`;
  } else if (count >= 100000) { // 1 lakh
    return `${(count / 100000).toFixed(1)}L`;
  } else if (count >= 1000) { // 1K
    return `${(count / 1000).toFixed(0)}K`;
  }
  return count.toString();
};

export const generateQuoteNumber = (): string => {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, ''); // YYYYMMDD
  const timeStr = now.getHours().toString().padStart(2, '0') + now.getMinutes().toString().padStart(2, '0');
  const randomStr = Math.random().toString(36).substr(2, 3).toUpperCase();
  return `QT-${dateStr}-${timeStr}-${randomStr}`;
};

export const createQuoteData = (
  customerName: string,
  companyName: string,
  emailVolume: number,
  pricing: PricingResult,
  discountReason?: string,
  generatedBy?: { name: string; email: string }
): QuoteData => {
  return {
    quoteNumber: generateQuoteNumber(),
    quoteDate: new Date().toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }),
    customerName,
    companyName,
    emailVolume,
    pricing,
    discountReason,
    generatedBy
  };
};